import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Input, InputNumber, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import type { FilterConfig } from './FilterBar';

interface BaseFilterOpts {
  key: string;
  label: string;
  placeholder?: string;
  width?: number | string;
}

export interface SearchFilterOpts extends BaseFilterOpts {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  debounceMs?: number;
}

const DebouncedSearchInput = React.memo(function DebouncedSearchInput({
  value, onChange, onSearch, placeholder, width, debounceMs = 500,
}: {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  width?: number | string;
  debounceMs?: number;
}) {
  const [local, setLocal] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setLocal(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChangeRef.current(v), debounceMs);
  };

  const handleSearch = (searchValue: string) => {
    clearTimeout(timerRef.current);
    onChangeRef.current(searchValue);
    onSearch?.(searchValue);
  };

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <Input.Search
      placeholder={placeholder}
      value={local}
      allowClear
      style={{ width: width ?? 220 }}
      onChange={handleChange}
      onSearch={handleSearch}
    />
  );
});

export function createSearchFilter(o: SearchFilterOpts): FilterConfig {
  return {
    key: o.key,
    label: o.label,
    type: 'text',
    render: () => (
      <DebouncedSearchInput
        value={o.value}
        onChange={o.onChange}
        onSearch={o.onSearch}
        placeholder={o.placeholder}
        width={o.width}
        debounceMs={o.debounceMs}
      />
    ),
  };
}

export interface SelectFilterOpts extends BaseFilterOpts {
  value: string | undefined;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
}

export function createSelectFilter(o: SelectFilterOpts): FilterConfig {
  return {
    key: o.key,
    label: o.label,
    type: 'select',
    render: () => (
      <Select
        aria-label={o.label}
        placeholder={o.placeholder ?? o.label}
        allowClear
        style={{ width: o.width ?? '100%' }}
        /*
         * Panel tự giãn theo nội dung, KHÔNG bám chiều rộng ô lọc.
         *
         * Ô lọc bị ép hẹp cho vừa hàng filter (vd "Trạng thái" ở /shipping rộng
         * 150px), mà mặc định của antd là panel rộng đúng bằng ô — nhãn dài bị
         * cắt thành "Đang giao hàng…" và người dùng phải đoán. Chọn nhầm trạng
         * thái vì đoán sai thì lọc ra sai danh sách.
         *
         * Cùng cách 5 component select khác trong repo đã làm
         * (`AccountSelect`, `WarehouseSelect`, …).
         */
        popupMatchSelectWidth={false}
        value={o.value || undefined}
        options={o.options}
        onChange={(v) => o.onChange(v ?? '')}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  };
}

export interface MultiSelectFilterOpts extends BaseFilterOpts {
  /** Các giá trị đang chọn, nối bằng dấu phẩy — cùng dạng lưu trên URL của `useListPageState`. */
  value: string | undefined;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
}

/**
 * Ô lọc chọn NHIỀU giá trị (TCR-2221). Giá trị vào/ra là chuỗi `a,b` để đi thẳng qua URL và
 * query string; máy chủ tách lại thành mảng.
 */
export function createMultiSelectFilter(o: MultiSelectFilterOpts): FilterConfig {
  const selected = (o.value ?? '').split(',').map((item) => item.trim()).filter(Boolean);
  return {
    key: o.key,
    label: o.label,
    type: 'select',
    render: () => (
      <Select
        mode="multiple"
        aria-label={o.label}
        placeholder={o.placeholder ?? o.label}
        allowClear
        /*
         * Luôn hiện ít nhất MỘT thẻ + "+N". `responsive` dồn hết vào "+2 …" khi ô hẹp — người
         * dùng không biết mình đang lọc theo gì. Rê chuột vào "+N" để xem đủ danh sách.
         */
        maxTagCount={1}
        maxTagTextLength={14}
        maxTagPlaceholder={(omitted) => (
          <span title={omitted.map((item) => String(item.label ?? item.value)).join(', ')}>+{omitted.length}</span>
        )}
        style={{ width: o.width ?? '100%' }}
        // Cùng lý do với `createSelectFilter`: nhãn dài không bị cắt trong panel.
        popupMatchSelectWidth={false}
        value={selected}
        options={o.options}
        onChange={(values: string[]) => o.onChange(values.join(','))}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  };
}

export interface NumberFilterOpts extends BaseFilterOpts {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
}

export function createNumberFilter(o: NumberFilterOpts): FilterConfig {
  return {
    key: o.key,
    label: o.label,
    type: 'number',
    render: () => (
      <InputNumber
        placeholder={o.placeholder ?? o.label}
        value={o.value}
        min={o.min}
        max={o.max}
        style={{ width: o.width ?? 120 }}
        onChange={(v) => o.onChange(v ?? undefined)}
      />
    ),
  };
}

export interface DateRangeFilterOpts extends Omit<BaseFilterOpts, 'placeholder'> {
  value: [Dayjs | null, Dayjs | null] | null;
  onChange: (range: [Dayjs | null, Dayjs | null] | null) => void;
  placeholder?: [string, string];
  picker?: 'date' | 'week' | 'month' | 'quarter' | 'year';
  format?: string;
}

// SafeRangePicker giúp memoize tham chiếu đối tượng Dayjs dựa trên valueOf.
// Điều này ngăn chặn lỗi "Maximum update depth exceeded" của Antd RangePicker khi component cha re-render liên tục.
const SafeRangePicker = ({
  value,
  onChange,
  style,
  placeholder,
  picker,
  format,
}: {
  value: [Dayjs | null, Dayjs | null] | null;
  onChange: (range: [Dayjs | null, Dayjs | null] | null) => void;
  style?: React.CSSProperties;
  placeholder?: [string, string];
  picker?: 'date' | 'week' | 'month' | 'quarter' | 'year';
  format?: string;
}) => {
  const memoizedValue = useMemo(() => {
    if (!value) return null;
    return [value[0], value[1]] as [Dayjs | null, Dayjs | null];
  }, [value?.[0]?.valueOf(), value?.[1]?.valueOf()]);

  return (
    <DatePicker.RangePicker
      style={style}
      value={memoizedValue as [Dayjs, Dayjs] | null}
      onChange={(v) => onChange(v as [Dayjs | null, Dayjs | null] | null)}
      placeholder={placeholder}
      picker={picker}
      format={format}
    />
  );
};

export function createDateRangeFilter(o: DateRangeFilterOpts): FilterConfig {
  return {
    key: o.key,
    label: o.label,
    type: 'daterange',
    render: () => (
      <SafeRangePicker
        style={{ width: o.width ?? 240 }}
        value={o.value}
        onChange={o.onChange}
        placeholder={o.placeholder}
        picker={o.picker}
        format={o.format}
      />
    ),
  };
}
